/**
 * Database client for Neon PostgreSQL via API endpoints
 * Replaces Supabase calls with HTTP API calls
 */

function getToken() {
  return localStorage.getItem('amlguard_token') || 'local-mode';
}

function getHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

class QueryBuilder {
  constructor(table, operation = 'select') {
    this.table = table;
    this.operation = operation;
    this.params = {};
    this.body = {};
    this.filters = [];
    this.orderBy = null;
    this.limitValue = null;
    this._maybeSingle = false;
  }

  select(columns = '*') {
    this.operation = 'select';
    this.params.columns = columns;
    return this;
  }

  insert(data) {
    this.operation = 'insert';
    this.body = data;
    return this;
  }

  update(data) {
    this.operation = 'update';
    this.body = data;
    return this;
  }

  delete() {
    this.operation = 'delete';
    return this;
  }

  eq(column, value) {
    if (this.operation === 'update' || this.operation === 'delete') {
      this.filters.push({ column, value, operator: 'eq' });
    } else {
      this.params[column] = value;
    }
    return this;
  }

  in(column, values) {
    if (this.operation === 'update' || this.operation === 'delete') {
      this.filters.push({ column, values, operator: 'in' });
    }
    return this;
  }

  not(column, value) {
    if (this.operation === 'select') {
      this.params[`not_${column}`] = value;
    }
    return this;
  }

  ilike(column, value) {
    if (this.operation === "select") {
      this.params[`ilike_${column}`] = value;
    }
    return this;
  }

  or(filter) {
    if (this.operation === "select") {
      this.params[`or_filter`] = filter;
    }
    return this;
  }

  order(column, options = {}) {
    // Support both old API: order('col', true) and new API: order('col', { ascending: true })
    const ascending = typeof options === 'object' ? (options.ascending ?? false) : options;
    this.orderBy = { column, ascending };
    return this;
  }

  limit(value) {
    this.limitValue = value;
    return this;
  }

  single() {
    this.limitValue = 1;
    return this;
  }

  maybeSingle() {
    this.limitValue = 1;
    this._maybeSingle = true;
    return this;
  }

  then(onFulfilled, onRejected) {
    return this.execute().then(onFulfilled, onRejected);
  }

  catch(onRejected) {
    return this.execute().catch(onRejected);
  }

  async execute() {
    const token = getToken();
    if (!token || token === 'local-mode') {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    try {
      const url = `/api/query?resource=${this.table}`;
      const method = this.operation === 'select' ? 'GET' : this.operation === 'insert' ? 'POST' : 'PUT';

      if (this.operation === 'delete') {
        return await this._executeDelete();
      }

      if (this.operation === 'select') {
        try {
          const params = new URLSearchParams();

          // Add columns to select if specified
          if (this.params.columns && this.params.columns !== '*') {
            params.append('columns', this.params.columns);
          }

          if (this.orderBy) {
            params.append('order_by', this.orderBy.column);
            params.append('order', this.orderBy.ascending ? 'asc' : 'desc');
          }
          if (this.limitValue) {
            params.append('limit', this.limitValue);
          }

          // Merge all filters into a single object, tracking which columns use ilike
          const filters = {};
          const ilikeColumns = [];
          for (const [key, value] of Object.entries(this.params)) {
            if (key !== 'columns') {
              if (key.startsWith('ilike_')) {
                const column = key.replace('ilike_', '');
                filters[column] = value;
                ilikeColumns.push(column);
              } else if (key === 'or_filter') {
                params.append('or_filter', value);
              } else {
                filters[key] = value;
              }
            }
          }

          // Add single merged filter parameter
          if (Object.keys(filters).length > 0) {
            if (ilikeColumns.length > 0) {
              // Mark which columns use ilike
              filters.__ilike_columns = ilikeColumns;
            }
            params.append('filter', JSON.stringify(filters));
          }

          const fullUrl = params.toString() ? `${url}&${params.toString()}` : url;
          const res = await fetch(fullUrl, {
            method: 'GET',
            headers: getHeaders(),
          });

          if (!res.ok) {
            const errText = await res.text().catch(() => 'Unknown error');
            console.error(`[DB] Query error on ${this.table}:`, errText);
            return { data: [], error: { message: errText } };
          }

          const responseData = await res.json().catch(err => {
            console.error(`[DB] JSON parse error for ${this.table}:`, err);
            return null;
          });

          if (!responseData) {
            return { data: [], error: { message: 'Invalid JSON response' } };
          }

          let result = Array.isArray(responseData) ? responseData : (responseData ? [responseData] : []);
          // If maybeSingle, return first item or null instead of array
          if (this._maybeSingle) {
            return { data: result.length > 0 ? result[0] : null, error: null };
          }
          return { data: result, error: null };
        } catch (selectErr) {
          console.error(`[DB] Select error on ${this.table}:`, selectErr);
          return { data: this._maybeSingle ? null : [], error: { message: selectErr.message } };
        }
      }

      if (this.operation === 'insert') {
        try {
          const res = await fetch(url, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(this.body),
          });

          if (!res.ok) {
            const errText = await res.text().catch(() => 'Unknown error');
            return { data: null, error: { message: errText } };
          }

          const insertedData = await res.json().catch(err => {
            console.error(`[DB] JSON parse error on insert for ${this.table}:`, err);
            return null;
          });
          return { data: insertedData, error: null };
        } catch (insertErr) {
          console.error(`[DB] Insert error on ${this.table}:`, insertErr);
          return { data: null, error: { message: insertErr.message } };
        }
      }

      if (this.operation === 'update') {
        try {
          const updateBody = { ...this.body };
          let updateUrl = url;

          if (this.filters.length > 0) {
            const eqFilters = this.filters.filter(f => f.operator === 'eq');
            const inFilter = this.filters.find(f => f.operator === 'in');

            if (inFilter) {
              updateBody.ids = inFilter.values;
            } else if (eqFilters.length > 0) {
              // Build filter object from eq conditions and pass as query parameter
              const filterObj = {};
              eqFilters.forEach(f => {
                filterObj[f.column] = f.value;
              });
              const params = new URLSearchParams();
              params.append('filter', JSON.stringify(filterObj));
              updateUrl = `${url}&${params.toString()}`;
            }
          }

          const res = await fetch(updateUrl, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(updateBody),
          });

          if (!res.ok) {
            const errText = await res.text().catch(() => 'Unknown error');
            return { data: null, error: { message: errText } };
          }

          const updatedData = await res.json().catch(err => {
            console.error(`[DB] JSON parse error on update for ${this.table}:`, err);
            return null;
          });
          return { data: updatedData, error: null };
        } catch (updateErr) {
          console.error(`[DB] Update error on ${this.table}:`, updateErr);
          return { data: null, error: { message: updateErr.message } };
        }
      }
    } catch (error) {
      return { data: null, error };
    }
  }

  async _executeDelete() {
    const url = `/api/query?resource=${this.table}`;
    const body = {};
    const filterObj = {};

    for (const filter of this.filters) {
      if (filter.operator === 'eq') {
        filterObj[filter.column] = filter.value;
        if (filter.column === 'id') {
          body.id = filter.value;
        }
      } else if (filter.operator === 'in') {
        filterObj[filter.column] = filter.values;
        if (filter.column === 'id') {
          body.ids = filter.values;
        }
      }
    }

    // Add filter object to body if we have any filters
    if (Object.keys(filterObj).length > 0 && !body.id && !body.ids) {
      body.filter = filterObj;
    }

    console.log('[dbClient] DELETE to', url, 'with body:', body);

    try {
      const res = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error('[dbClient] DELETE error response:', errText);
        return { data: null, error: { message: errText } };
      }

      const data = await res.json();
      return { data, error: null };
    } catch (error) {
      console.error('[dbClient] DELETE exception:', error);
      return { data: null, error };
    }
  }
}

class DatabaseClient {
  from(table) {
    return new QueryBuilder(table);
  }

  async upsert(table, data, options = {}) {
    const token = getToken();
    if (!token || token === 'local-mode') {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    try {
      const { id, company_id, ...updateFields } = data;

      // Build filter for update based on company_id or id
      const filter = id ? { id } : (company_id ? { company_id } : null);

      if (!filter) {
        return { data: null, error: { message: 'id or company_id required for upsert' } };
      }

      // Try update first - don't stringify JSON, PostgreSQL handles it
      const updateUrl = `/api/query?resource=${table}`;
      const updateRes = await fetch(updateUrl, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          ...updateFields,
          filter: filter,
        }),
      });

      if (updateRes.ok) {
        const updatedData = await updateRes.json().catch(err => {
          console.error(`[DB] JSON parse error on upsert for ${table}:`, err);
          return null;
        });
        // Return the updated data (could be empty if no rows matched)
        if (updatedData) {
          return { data: updatedData, error: null };
        }
      }

      // If update returned no rows or failed, try insert
      const insertUrl = `/api/query?resource=${table}`;
      const insertRes = await fetch(insertUrl, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });

      if (!insertRes.ok) {
        const errText = await insertRes.text().catch(() => 'Unknown error');
        return { data: null, error: { message: errText } };
      }

      const insertedData = await insertRes.json().catch(err => {
        console.error(`[DB] JSON parse error on upsert insert for ${table}:`, err);
        return null;
      });

      return { data: insertedData, error: null };
    } catch (upsertErr) {
      console.error(`[DB] Upsert error on ${table}:`, upsertErr);
      return { data: null, error: { message: upsertErr.message } };
    }
  }
}

export const db = new DatabaseClient();
